import isEqual from "lodash/isEqual";
import { cyclicRangeInclusive, mod } from "../../util/basicAlgebra";
import { algorithms, hotspotDisplayStates, steps } from "../../util/constants";
import { defaultBox } from "./hotspotEditorReducers";
import { difference, intersection, max } from "lodash";
import { getIntegerKeys, moveToFront } from "../../util/general";

export const getNaturalHeight = (state) => state.naturalHeight;
export const getNaturalWidth = (state) => state.naturalWidth;
export const getNaturalAspect = (state) =>
  getNaturalHeight(state) / getNaturalWidth(state);

export const getBestAvailableImageUrlByFilename = (state, { filename }) => {
  const urls = state?.imageURLs?.[filename]?.urlsAvailable;
  if (!urls) {
    return null;
  }
  if ("" in urls) {
    return urls[""].url;
  } // full resolution
  const keys = Object.keys(urls).map((x) => parseInt(x, 10));
  const key = max(keys);
  return urls[key].url;
};

export const getBestRequestedImageKeysByFilename = (state, { filename }) => {
  const urls = state?.imageURLs?.[filename]?.urlsRequested;
  if (!urls) {
    return null;
  }
  if ("" in urls) {
    return "";
  } // full resolution
  const keys = Object.keys(urls).map((x) => parseInt(x, 10));
  const key = max(keys);
  return key;
};

export const getHS = (state, options) => {
  const { hotspotIndex = getCurrentHotspot(state) } = options ?? {};
  return state.hotspots?.[hotspotIndex];
};
export const getRequestedAlgorithm = (state, options) => {
  const { hotspotIndex = getCurrentHotspot(state) } = options ?? {};
  const hs = getHS(state, hotspotIndex);
  return hs?.requestedAlgorithm;
};
export const getCommittedTheme = (state) => state.committedTheme;
export const getPointerText = (state, options = {}) => {
  const { hotspotIndex = state.currentHotspot } = options;
  return state.hotspots[hotspotIndex]?.pointerText;
};
export const getCurrentHotspot = (state) => state.currentHotspot;
export const getCurrentLayer = (state) => state.currentLayer;
export const getHotspotName = (state, options) => {
  const { hotspotIndex = getCurrentHotspot(state) } = options ?? {};
  return state.hotspots?.[hotspotIndex]?.name;
};
export const getActiveAlgorithm = (state, options) => {
  const { hotspotIndex = getCurrentHotspot(state) } = options ?? {};
  return state?.hotspots?.[hotspotIndex]?.activeAlgorithm;
};

export const getCheckpointKeyFrameCount = (state) => {
  const hotspot = state.checkpoint;
  const layer = getCurrentLayer(state);
  return (
    hotspot?.algorithmInput?.[hotspot?.activeAlgorithm]?.[layer].length ?? 0
  );
};

export const hotspotVisibleInFrame = (state, options = {}) => {
  const {
    frame = getCurrentFrameIndex(state),
    hotspotIndex = getCurrentHotspot(state),
  } = options;
  const hidden = getHidden(state, options);
  return isWithinBounds(state, { frame, hotspotIndex }) && hidden !== true;
};
export const getHidden = (state, options) => {
  const {
    hotspotIndex = getCurrentHotspot(state),
    frame = getCurrentFrameIndex(state),
    layer = getCurrentLayer(state),
  } = options ?? {};
  const hidden = state?.hotspots?.[hotspotIndex]?.hidden;
  return hidden?.[layer]?.[frame];
};
export const isWithinBounds = (state, options) => {
  const {
    frame,
    layer = getCurrentLayer(state),
    hotspotIndex = getCurrentHotspot(state),
  } = options ?? {};
  const [first, last] = getBounds(state, {
    hotspotIndex,
    layer,
  });
  // calculation for the "increase" case - frames increase by 1 from first too last and are "modulo N"
  let currentFrame = frame ?? getCurrentFrameIndex(state);
  currentFrame = parseInt(currentFrame, 10);
  if (last >= first) {
    return first <= currentFrame && currentFrame <= last;
  } else {
    return currentFrame <= last || currentFrame >= first;
  }
};
export const getBounds = (state, options) => {
  const {
    hotspotIndex = getCurrentHotspot(state),
    layer = getCurrentLayer(state),
  } = options ?? {};
  const bounds = state.hotspots?.[hotspotIndex]?.bounds?.[layer] ?? [];
  if (bounds.length > 2) {
    throw new Error("Application code error, should never happen");
  }
  return bounds;
};

export const getKeyFrameSequentialIndices = (state, options) => {
  const {
    hotspotIndex = getCurrentHotspot(state),
    layer = getCurrentLayer(state),
  } = options ?? {};
  if (!state?.hotspots?.length) {
    return [];
  }
  const kf = getUserApprovedLocations(state, { hotspotIndex });
  return kf?.[layer] ? Object.keys(kf[layer]) : [];
};

export const getKeyFrameCount = (state, options) => {
  const keyframeIndices = getKeyFrameSequentialIndices(state, options);
  return keyframeIndices.length;
};

export const getFitReadyState = (
  state,
  hotspotIndex = getCurrentHotspot(state),
  layer = getCurrentLayer(state)
) => {
  return getKeyFrameCount(state, { hotspotIndex, layer }) >= 3;
};

export const isAlgorithmSwitchPossible = (
  state,
  { from = getActiveAlgorithm(state), to }
) => {
  if ([from, to].includes(undefined)) {
    return false;
  }
  function switchToSpinPossible() {
    const layers = getAlgorithmOutput(state, { algorithm: from });
    const keyframes = layers[getCurrentLayer(state)] ?? {};
    const count = Object.keys(keyframes).length;
    return count >= 3;
  }
  function yes() {
    return true;
  }

  const transitionMatrix = {
    [algorithms.manual]: {
      [algorithms.spin]: switchToSpinPossible,
    },
    [algorithms.spin]: {
      [algorithms.manual]: yes,
    },
  };

  return transitionMatrix[from][to]();
};

export const getInstructions = (state) => {
  const keyFrameCount = getKeyFrameCount(state);
  const hotspotIndex = getCurrentHotspot(state);
  if (hotspotIndex === null) {
    return { stage: 0 };
  }
  const requestedAlgorithm = getRequestedAlgorithm(state);
  if (requestedAlgorithm === algorithms.manual) {
    return { stage: "manual" };
  }

  if ([0, 1, 2].includes(keyFrameCount)) {
    return {
      stage: keyFrameCount,
    };
  }
  if (keyFrameCount >= 3) {
    return { stage: "done" };
  }
  throw new Error("Invalid frame count");
};

export const getThumbnailFrameIndex = (state, { hotspotIndex }) => {
  const keyFrames = getKeyFrameSequentialIndices(state, { hotspotIndex });
  const n = keyFrames.length;
  if (n === 0) {
    return null;
  }
  return keyFrames[Math.min(n - 1, 1)]; //second keyFrame if available
};

export const getVisibleFramesInLayer = (state, options) => {
  const {
    hotspotIndex = getCurrentHotspot(state),
    layer = getCurrentLayer(state),
  } = options ?? {};
  const N = getFrameCount(state);
  const boundaries = getBounds(state, { hotspotIndex, layer });
  const inBounds = cyclicRangeInclusive({ N, boundaries });
  const hiddenBoolean = state.hotspots[hotspotIndex].hidden[layer] ?? [];
  const hiddenIndices = hiddenBoolean
    .map((x, i) => (x ? i : null))
    .filter((x) => x === 0 || x);
  const visible = difference(inBounds, hiddenIndices);
  const algorithmOutput = getAlgorithmOutput(state, { hotspotIndex });
  const outputFrames = getIntegerKeys(algorithmOutput?.[layer] ?? {}).filter(
    (x) => x === 0 || x
  );
  return intersection(outputFrames, visible);
};

export const getInvisibleFrames = (state, options) => {
  const { hotspotIndex = getCurrentHotspot(state) } = options ?? {};
  const N = getFrameCount(state);
  const [b0, b1] = getBounds(state, { hotspotIndex });
  const boundaries = [mod(b1 + 1, N), mod(b0 - 1, N)];
  return cyclicRangeInclusive({ N, boundaries });
};

export const getCurrentStep = (state) => state.currentStep;
export const getPreviousStep = (state) => state.previousStep;
export const getWidth = (state) => state.width;
export const getHeight = (state) => state.height;
export const getCurrentFrameIndex = (state) => state.currentFrameIndex;
export const getAlgorithmOutput = (state, options = {}) => {
  const {
    hotspotIndex = getCurrentHotspot(state),
    algorithm = getActiveAlgorithm(state, { hotspotIndex }),
  } = options;
  const hs = state.hotspots[hotspotIndex];
  const output = hs?.algorithmState?.[algorithm]?.output ?? {};
  return output;
};

export const getCalculatedHotspotCoordinates = (state, options = {}) => {
  const {
    layer = getCurrentLayer(state),
    frame = getCurrentFrameIndex(state),
  } = options;
  const algorithmOutput = getAlgorithmOutput(state, options);
  return algorithmOutput?.[layer]?.[frame];
};
export const getAlgorithmState = (
  state,
  {
    hotspotIndex = getCurrentHotspot(state),
    algorithm = getActiveAlgorithm(state, { hotspotIndex }),
  }
) => {
  const hs = state.hotspots[hotspotIndex];
  const algoState = hs.algorithmState[algorithm];
  return algoState;
};

export const getUserApprovedLocations = (state, options) => {
  const { hotspotIndex = getCurrentHotspot(state) } = options ?? {};
  const hs = state.hotspots[hotspotIndex];
  return hs?.userApprovedLocations;
};

const getHotspotDisplayStateBasic = (state, options) => {
  const {
    hotspotIndex = getCurrentHotspot(state),
    layer = getCurrentLayer(state),
    frame = getCurrentFrameIndex(state),
  } = options ?? {};
  const algorithm = getActiveAlgorithm(state, { hotspotIndex });
  const output = getAlgorithmOutput(state, { hotspotIndex, algorithm });
  if (!output?.[layer]?.[frame]) {
    return hotspotDisplayStates.none;
  }
  if ([algorithms.manual].includes(algorithm)) {
    return hotspotDisplayStates.normal;
  }
  const approvedLocations = getUserApprovedLocations(state, { hotspotIndex });
  const approved = approvedLocations?.[layer]?.[frame] !== undefined;
  const inBounds = isWithinBounds(state, { hotspotIndex, layer, frame });
  const step = getCurrentStep(state);
  const locationMode = step === steps.configureLocations;
  const isCurrent = hotspotIndex === getCurrentHotspot(state);
  if (inBounds) {
    if (!locationMode || !isCurrent) {
      return hotspotDisplayStates.normal;
    }
    if (approved) {
      return hotspotDisplayStates.approved;
    }
    return hotspotDisplayStates.accepted;
  } else {
    if (!locationMode || !isCurrent) {
      return hotspotDisplayStates.none;
    }
    return hotspotDisplayStates.proposed;
  }
};

export const getHotspotDisplayStateLevel2 = (state, options = {}) => {
  const basicState = getHotspotDisplayStateBasic(state, options);
  const isHidden = getHidden(state, options);
  if (isHidden) {
    switch (basicState) {
      case hotspotDisplayStates.approved:
        return hotspotDisplayStates.hiddenApproved;
      case hotspotDisplayStates.accepted:
        return hotspotDisplayStates.hiddenAccepted;
      case hotspotDisplayStates.normal:
        return hotspotDisplayStates.none;
      default:
        return basicState;
    }
  }
  const { hotspotIndex } = options;
  const step = getCurrentStep(state);
  const current = [getCurrentHotspot(state), undefined].includes(hotspotIndex);
  if (
    [steps.configureLocations].includes(step) &&
    current &&
    basicState === hotspotDisplayStates.normal
  ) {
    return hotspotDisplayStates.currentNormal;
  }
  return basicState;
};

export const getHotspotDisplayState = (state, options) => {
  const {
    layer = getCurrentLayer(state),
    frame = getCurrentFrameIndex(state),
  } = options ?? {};
  const level2state = getHotspotDisplayStateLevel2(state, options);
  const inFrame =
    layer === getCurrentLayer(state) && frame === getCurrentFrameIndex(state);
  if (!inFrame) {
    switch (level2state) {
      case hotspotDisplayStates.accepted:
        return hotspotDisplayStates.outOfFrameAccepted;
      case hotspotDisplayStates.approved:
        return hotspotDisplayStates.outOfFrameApproved;
      case hotspotDisplayStates.proposed:
        return hotspotDisplayStates.outOfFrameProposed;
      case hotspotDisplayStates.currentNormal:
        return hotspotDisplayStates.outOfFrameCurrentNormal;
      case hotspotDisplayStates.hiddenAccepted:
        return hotspotDisplayStates.outOfFrameHiddenAccepted;
      case hotspotDisplayStates.hiddenApproved:
        return hotspotDisplayStates.outOfFrameHiddenApproved;
      default:
        return level2state;
    }
  }
  return level2state;
};

export const getHotspotNames = (state) => {
  return state.hotspots.map((v) => v.name);
};

export const getHotspotCount = (state) => {
  return state.hotspots.length;
};

export const getTooltipIsDefault = (state, options) => {
  const { hotspotIndex } = options;
  const s = state.hotspots?.[hotspotIndex];
  return !(s.tooltip.content || s.tooltip.style !== "default");
};

export const getClickActionIsDefault = (state, options) => {
  const { hotspotIndex } = options;
  const hs = state.hotspots?.[hotspotIndex];
  const actionType = hs.activeClickAction;
  switch (actionType) {
    case "link":
      return hs.link === "";
    case "box":
      return isEqual(hs.box, defaultBox);
    case "none":
      return true;
    default:
      console.error(`Unknown click action type ${actionType}`);
  }
};

/**
 * Retrieves the filenames within a specified layer of the application state. If no layer is specified,
 * the current layer is used. Filenames can optionally be reordered to place the current frame's filename at the beginning.
 *
 * @param {Object} state - The application state containing layers and filenames.
 * @param {Object} [options={}] - Optional parameters to modify the behavior of the selector.
 * @param {string} [options.layer] - The layer for which filenames are retrieved. Defaults to the current layer.
 * @param {boolean} [options.currentFirst=false] - If true, the current frame's filename is moved to the front of the returned array.
 * @returns {Array} An array of filenames for the specified layer. If `currentFirst` is true and a current frame is identified,
 *                  the array is reordered to start with the current frame's filename.
 *
 * @example
 * // Assuming the current layer is 'layer1' and the current frame index is 2,
 * // and state.filenames.layer1 = ['file1.png', 'file2.png', 'file3.png']
 * getFilenamesInLayer(state, { currentFirst: true });
 * // Returns: ['file3.png', 'file1.png', 'file2.png']
 *
 * @example
 * // Specifying a layer without reordering
 * getFilenamesInLayer(state, { layer: 'layer2' });
 * // Returns filenames array for 'layer2', e.g., ['layer2file1.png', 'layer2file2.png']
 */
export const getFilenamesInLayer = (state, options = {}) => {
  const { layer = getCurrentLayer(state), currentFirst = false } = options;
  const filenamesInLayer = state.filenames[layer];
  if (currentFirst) {
    const currentFrameIndex = getCurrentFrameIndex(state);
    if (currentFrameIndex > 0) {
      return moveToFront(filenamesInLayer, currentFrameIndex);
    }
  }
  return filenamesInLayer;
};

export const getFrameCount = (state, options = {}) => {
  const { layer = getCurrentLayer(state) } = options;

  const filenames = getFilenamesInLayer(state, { layer });
  return filenames.length;
};

export const getHotspotIndexOfClickMessageShown = (state) =>
  state.hotspotIndexOfClickMessageShown;
export const getHotspotIndexOfHoverMessageShown = (state) =>
  state.hotspotIndexOfHoverMessageShown;

export const getFilename = (state, options = {}) => {
  const {
    frame = getCurrentFrameIndex(state),
    layer = getCurrentLayer(state),
  } = options;
  const filenames = getFilenamesInLayer(state, { layer });
  return filenames[frame];
};

// == EXPORT ==
export const getHoverActionType = (state, inputHotspotIndex) => {
  const hotspotIndex = inputHotspotIndex ?? getCurrentHotspot(state);
  return state.hotspots[hotspotIndex].activeHoverAction;
};

export const getHoverActionTooltipContent = (state, options) => {
  const { hotspotIndex = getCurrentHotspot(state) } = options ?? {};
  return state.hotspots[hotspotIndex]?.tooltip?.content;
};

export const getHoverActionTooltipStyle = (state, options) => {
  const { hotspotIndex = getCurrentHotspot(state) } = options ?? {};
  return state.hotspots[hotspotIndex]?.tooltip?.style;
};

export const getClickActionType = (state, inputHotspotIndex) => {
  const hotspotIndex = inputHotspotIndex ?? getCurrentHotspot(state);
  return state.hotspots[hotspotIndex]?.activeClickAction;
};

export const getClickActionLink = (state, options) => {
  const { hotspotIndex = getCurrentHotspot(state) } = options ?? {};
  return state.hotspots[hotspotIndex]?.link ?? "";
};

export const getClickActionBoxContent = (state, inputHotspotIndex) => {
  const hotspotIndex = inputHotspotIndex ?? getCurrentHotspot(state);
  return state.hotspots[hotspotIndex]?.box?.content;
};

export const getLoadedClickActionBoxStyle = (
  state,
  { hotspotIndex = getCurrentHotspot(state) }
) => {
  return state.hotspots[hotspotIndex]?.box?.style;
};

export const getClickActionBoxLocationType = (state, options) => {
  const { hotspotIndex = getCurrentHotspot(state) } = options ?? {};
  return state.hotspots[hotspotIndex]?.box?.location?.type;
};

export const getClickActionBoxLocationFollowXOffset = (state, options) => {
  const { hotspotIndex = getCurrentHotspot(state) } = options ?? {};
  return state.hotspots[hotspotIndex]?.box?.location?.follow?.x;
};

export const getClickActionBoxLocationFollowYOffset = (state, options) => {
  const { hotspotIndex = getCurrentHotspot(state) } = options ?? {};
  return state.hotspots[hotspotIndex]?.box?.location?.follow?.y;
};

export const getClickActionBoxLocationFixedXKeyword = (state, options) => {
  const { hotspotIndex = getCurrentHotspot(state) } = options ?? {};
  return state.hotspots[hotspotIndex]?.box?.location?.fixed?.x?.keyword;
};

export const getClickActionBoxLocationFixedYKeyword = (state, options) => {
  const { hotspotIndex = getCurrentHotspot(state) } = options ?? {};
  return state.hotspots[hotspotIndex]?.box?.location?.fixed?.y?.keyword;
};

export const getClickActionBoxLocationFixedXOffset = (state, options) => {
  const { hotspotIndex = getCurrentHotspot(state) } = options ?? {};
  return state.hotspots[hotspotIndex]?.box?.location?.fixed?.x?.offset;
};

export const getClickActionBoxLocationFixedYOffset = (state, options) => {
  const { hotspotIndex = getCurrentHotspot(state) } = options ?? {};
  return state.hotspots[hotspotIndex]?.box?.location?.fixed?.y?.offset;
};

export const getAccepted = (state, options) => {
  const coordinates = getCalculatedHotspotCoordinates(state, options);
  const inBounds = isWithinBounds(state, options);
  const accepted = coordinates && inBounds;
  return accepted;
};

export const getNotification = (state) => state.notification;
